import { FC, ReactElement, useCallback, useMemo } from 'react'

import { memberInfoGuard } from 'guards/globalGuards'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { isPIIVerifiedSelector } from 'selectors/creditSelectors'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { hasFirstLinkedAccountSelector, memberSelector } from 'selectors/globalSelectors'
import { WaysToEarnRow } from 'components/features/rewards/rewardPage/WaysToEarn/WaysToEarnRow'
import Button from 'components/features/pfm/Button/Button'
import { WaysToEarnBenefit } from 'components/features/rewards/rewardPage/WaysToEarn/WaysToEarnBenefit'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { activateAnchorActionCreator, pushActionCreator } from 'actions/navigationActions'
import { CREDIT_LINKAGE_URL } from 'tangle-shared'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { FLOATING_PANE_INVITE_FRIENDS_PANE } from 'components/features/inviteFriendsPane/constants/pane'
import { LinkAccountHandler } from 'components/features/pfm/LinkAccountHandler/LinkAccountHandler'

import styles from 'components/features/rewards/rewardPage/WaysToEarn/WaysToEarn.styl'
import { CREDIT_CARD_PAGE, OPPORTUNITIES_PAGE } from 'constants/pages'
import { setFilterCategoryActionCreator } from 'actions/globalActions'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import Badge from 'components/features/insights/Insight/Badge'

type WaysToEarnReadyProps = {
  hasFirstLinkedAccount: boolean
  isPIIVerified: boolean
  isMobileLayout: boolean
}

const BENEFIT_COMMON_PRICE = '$1'
const BENEFIT_EARNED_COMMON_LABEL = `${ BENEFIT_COMMON_PRICE } Earned`
const BENEFIT_REWARD_COMMON_LABEL = `Earn ${ BENEFIT_COMMON_PRICE } instantly`

const WaysToEarnReady: FC<WaysToEarnReadyProps> = ({
  hasFirstLinkedAccount,
  isPIIVerified,
}): ReactElement => {
  const dispatch = useStoreDispatch()

  const openInviteFriendsPane = useCallback(() => {
    dispatch(modifyFloatingPaneActionCreator(
      FLOATING_PANE_INVITE_FRIENDS_PANE,
      { type: FLOATING_PANE_INVITE_FRIENDS_PANE }
    ))
  }, [dispatch])

  const navigateTo = useCallback((url: string): void => {
    dispatch(pushActionCreator(url))
  }, [dispatch])

  const renderButton = useCallback((label: string, onClick: () => void): ReactElement => (
    <Button size='mini' style='textOnly' onClick={ onClick } className={ styles.button }>{ label }</Button>
  ), [])

  const addLinkedAccountRowBenefitColumn = useMemo((): ReactElement => {
    const benefitLabel = hasFirstLinkedAccount
      ? BENEFIT_EARNED_COMMON_LABEL
      : BENEFIT_REWARD_COMMON_LABEL
    const handleLinkAccount = (): void => {
      // dispatch(
      //   startLinkageSessionActionCreator.request({
      //     cid: createCompositeId('manual-balance-account', null),
      //   })
      // )
    }
    const benefitButton = hasFirstLinkedAccount
      ? null
      : <LinkAccountHandler>{renderButton('Link account', handleLinkAccount)}</LinkAccountHandler>
    return (
      <WaysToEarnBenefit benefitLabelNode={ benefitLabel } benefitButtonNode={ benefitButton } />
    )
  }, [hasFirstLinkedAccount, renderButton])

  const addLinkedAccountRow = useMemo((): ReactElement => {
    return (
      <WaysToEarnRow
        isCompleted={ hasFirstLinkedAccount }
        whatToDoColumnNode='Link your first account'
        benefitColumnNode={ addLinkedAccountRowBenefitColumn }
      />
    )
  }, [hasFirstLinkedAccount, addLinkedAccountRowBenefitColumn])

  const verifyIdentityRowBenefitColumn = useMemo((): ReactElement => {
    const benefitLabel = isPIIVerified
      ? BENEFIT_EARNED_COMMON_LABEL
      : BENEFIT_REWARD_COMMON_LABEL
    const handleNavigateToLinkagePage = () => navigateTo(CREDIT_LINKAGE_URL)
    const benefitButton = isPIIVerified
      ? null
      : renderButton('Verify now', handleNavigateToLinkagePage)
    return (
      <WaysToEarnBenefit benefitLabelNode={ benefitLabel } benefitButtonNode={ benefitButton } />
    )
  }, [isPIIVerified, renderButton, navigateTo])

  const verifyIdentityRow = useMemo((): ReactElement => {
    return (
      <WaysToEarnRow
        isCompleted={ isPIIVerified }
        whatToDoColumnNode='Verify your identity and track your credit score'
        benefitColumnNode={ verifyIdentityRowBenefitColumn }
      />
    )
  }, [isPIIVerified, verifyIdentityRowBenefitColumn])

  const shareWithFriendsRowBenefitColumn = useMemo((): ReactElement => {
    const benefitLabel = 'Earn per invite'
    const benefitButton = renderButton('Invite friends', openInviteFriendsPane)
    return (
      <WaysToEarnBenefit benefitLabelNode={ benefitLabel } benefitButtonNode={ benefitButton } />
    )
  }, [openInviteFriendsPane, renderButton])

  const shareWithFriendsRow = useMemo((): ReactElement => {
    return (
      <WaysToEarnRow
        isCompleted={ false }
        whatToDoColumnNode={ `Share your referral link and earn ${ BENEFIT_COMMON_PRICE } for every person that joins and links their credit file and an account` }
        benefitColumnNode={ shareWithFriendsRowBenefitColumn }
      />
    )
  }, [shareWithFriendsRowBenefitColumn])

  const handleCategorySelect = useCallback(() => {
    dispatch(setFilterCategoryActionCreator(OPPORTUNITIES_PAGE, 'rewardable'))
    dispatch(activateAnchorActionCreator(OPPORTUNITIES_PAGE))
  }, [dispatch])

  const rewardBadgeRow = useMemo((): ReactElement => {
    const badge = (
      <div className={ styles.badgeWrapper }>
        <NavigationLink to={ OPPORTUNITIES_PAGE } onClick={ handleCategorySelect }>
          <Badge
            theme='golden'
            size='small'
          >
            Reward
          </Badge>
        </NavigationLink>
      </div>
    )
    const whatToDoNode = (
      <div>
        Find golden { badge }badges on opportunities and insights
      </div>
    )
    const cta = (
      <NavigationLink to={ OPPORTUNITIES_PAGE } onClick={ handleCategorySelect }>
        { renderButton('SEE OPPORTUNITIES', () => { /* do nothing */ }) }
      </NavigationLink>
    )
    return (
      <WaysToEarnRow
        isCompleted={ false }
        whatToDoColumnNode={ whatToDoNode }
        benefitColumnNode={ cta }
        className={ styles.opportunities }
      />
    )
  }, [handleCategorySelect, renderButton])

  const cardRowBenefitColumn = useMemo((): ReactElement => {
    const cta = (
      <NavigationLink to={ CREDIT_CARD_PAGE } onClick={ handleCategorySelect }>
        { renderButton('GET THE CARD', () => { /* do nothing */ }) }
      </NavigationLink>
    )
    return (
      <WaysToEarnBenefit benefitLabelNode='Earn per purchase' benefitButtonNode={ cta } />
    )
  }, [handleCategorySelect, renderButton])

  const cardRow = useMemo((): ReactElement => {
    return (
      <>
        <WaysToEarnRow
          isCompleted={ false }
          whatToDoColumnNode='Make purchases using the Status card to earn on every transaction'
          benefitColumnNode={ cardRowBenefitColumn }
        />
        <NavigationLink to={ CREDIT_CARD_PAGE } onClick={ handleCategorySelect }>
          <Button
            fullWidth
            size='medium'
            style='outlined'
          >
            EARN WITH THE STATUS CARD
          </Button>
        </NavigationLink>
      </>
    )
  }, [cardRowBenefitColumn, handleCategorySelect])

  return (
    <div className={ styles.container }>
      { addLinkedAccountRow }
      { verifyIdentityRow }
      { shareWithFriendsRow }
      { rewardBadgeRow }
      { cardRow }
    </div>
  )
}

export const WaysToEarn: FC = (): ReactElement => {
  useSimpleGuard(memberInfoGuard, {}, 'WaysToEarn')

  const member = useStoreSelector(memberSelector)
  const hasFirstLinkedAccount = useStoreSelector(hasFirstLinkedAccountSelector)
  const isPIIVerifiedOrNull = useStoreSelector(isPIIVerifiedSelector)
  const isMobileLayout = useStoreSelector(isMobileSelector)

  const isDataLoaded = !!member.data && hasFirstLinkedAccount != null && isPIIVerifiedOrNull != null

  const nodeToRender = useMemo<ReactElement>(() => {
    if (isDataLoaded) {
      return (
        <WaysToEarnReady
          isMobileLayout={ isMobileLayout }
          hasFirstLinkedAccount={ Boolean(hasFirstLinkedAccount) }
          isPIIVerified={ Boolean(isPIIVerifiedOrNull) }
        />
      )
    }
    return <LoadingOverlay />
  }, [isDataLoaded, hasFirstLinkedAccount, isPIIVerifiedOrNull, isMobileLayout])
  return nodeToRender
}
